import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/data/supabase';
import { carregarMembershipsComCache, type Membership } from '@/data/repository';
import { clearCache } from '@/data/db';
import { startSyncEngine } from '@/data/sync';

/**
 * Sessão + organização ativa.
 *
 * A organização ativa é CONVENIÊNCIA VISUAL. Nenhuma decisão de acesso é
 * tomada aqui: quem autoriza é a RLS no Postgres. `can()` existe apenas para
 * não mostrar botão que o servidor vai recusar.
 */

interface SessionValue {
  session: Session | null;
  loading: boolean;
  memberships: Membership[];
  orgId: string | null;
  org: Membership | null;
  setOrgId: (id: string) => void;
  can: (permission: string) => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshMemberships: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);
const ORG_KEY = 'nr12.activeOrg'; // localStorage: só preferência pequena

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [orgId, setOrgIdState] = useState<string | null>(null);

  const refreshMemberships = useCallback(async (usuarioId: string) => {
    try {
      const { memberships: rows } = await carregarMembershipsComCache(usuarioId);
      setMemberships(rows);
      setOrgIdState((current) => {
        if (current && rows.some((r) => r.organization_id === current)) return current;
        let stored: string | null = null;
        try {
          stored = localStorage.getItem(ORG_KEY);
        } catch {
          /* private mode / storage bloqueado */
        }
        if (stored && rows.some((r) => r.organization_id === stored)) return stored;
        return rows[0]?.organization_id ?? null;
      });
    } catch {
      setMemberships([]);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    void supabase.auth.getSession().then(async ({ data }) => {
      if (!alive) return;
      setSession(data.session);
      if (data.session) await refreshMemberships(data.session.user.id);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (s) void refreshMemberships(s.user.id);
      else setMemberships([]);
    });

    startSyncEngine();
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, [refreshMemberships]);

  const setOrgId = useCallback((id: string) => {
    setOrgIdState(id);
    try {
      localStorage.setItem(ORG_KEY, id);
    } catch {
      /* preferência é opcional; a app funciona sem */
    }
  }, []);

  const org = useMemo(
    () => memberships.find((m) => m.organization_id === orgId) ?? null,
    [memberships, orgId],
  );

  const can = useCallback(
    (permission: string) => Boolean(org?.permissions.includes(permission)),
    [org],
  );

  const value: SessionValue = {
    session,
    loading,
    memberships,
    orgId,
    org,
    setOrgId,
    can,
    async signIn(email, password) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data.user) await refreshMemberships(data.user.id);
    },
    async signUp(email, password, fullName) {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      });
      if (error) throw error;
    },
    async signOut() {
      // o CACHE de leitura sai; a FILA de mutações permanece intacta
      await clearCache();
      await supabase.auth.signOut();
    },
    // quem chama de fora não tem o id do usuário na mão; a sessão tem
    refreshMemberships: async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) await refreshMemberships(data.session.user.id);
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession precisa estar dentro de <SessionProvider>');
  return v;
}
